import { cloneElement, Fragment, isValidElement, type ReactElement, type ReactNode } from "react";
import {
  Bug,
  CheckCircle2,
  CircleAlert,
  CircleX,
  ExternalLink,
  FileText,
  Hourglass,
  Loader2,
  Play,
  RotateCcw,
  TriangleAlert,
} from "lucide-react";

import type { FactoriesFactoryPullRequest } from "@/api-client";
import { Link } from "@/components/Link/link";
import { Button } from "@/components/ui/button";
import { ButtonGroup, ButtonGroupSeparator } from "@/components/ui/button-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { MarkdownContent } from "@/pages/app/Markdown";
import { WorkOrderPersonMention } from "@/pages/app/markdownMentions";

import type { StartEmphasis } from "../../lib/draftReadiness";
import type { SplitRunDecisionTone, SplitRunFooterAction, SplitRunFooterNote } from "./splitRunFooter";
import { noteActionClassName, noteActionDisabled } from "./splitRunNoteActionStyle";
import { WaitingPullRequestReview } from "./SplitRunPullRequestReviewNote";

const TONE = {
  draft: {
    strip: "border-[color:var(--status-draft-border)] bg-[color:var(--status-draft-bg)]",
    iconWrap: "bg-[color:var(--status-draft-dot)]/15",
    icon: "text-[color:var(--status-draft-fg)]",
    Icon: FileText,
  },
  "draft-blocked": {
    strip: "border-[color:var(--status-failed-border)] bg-[color:var(--status-failed-bg)]",
    iconWrap: "bg-[color:var(--status-failed-dot)]/15",
    icon: "text-[color:var(--status-failed-fg)]",
    Icon: CircleAlert,
  },
  "draft-caution": {
    strip: "border-[color:var(--status-waiting-border)] bg-[color:var(--status-waiting-bg)]",
    iconWrap: "bg-[color:var(--status-waiting-dot)]/15",
    icon: "text-[color:var(--status-waiting-fg)]",
    Icon: TriangleAlert,
  },
  "draft-ready": {
    strip: "border-[color:var(--status-completed-border)] bg-[color:var(--status-completed-bg)]",
    iconWrap: "bg-[color:var(--status-completed-dot)]/15",
    icon: "text-[color:var(--status-completed-fg)]",
    Icon: CheckCircle2,
  },
  waiting: {
    strip: "border-[color:var(--status-waiting-border)] bg-[color:var(--status-waiting-bg)]",
    iconWrap: "bg-[color:var(--status-waiting-dot)]/15",
    icon: "text-[color:var(--status-waiting-fg)]",
    Icon: Hourglass,
  },
  failed: {
    strip: "border-[color:var(--status-failed-border)] bg-[color:var(--status-failed-bg)]",
    iconWrap: "bg-[color:var(--status-failed-dot)]/15",
    icon: "text-[color:var(--status-failed-fg)]",
    Icon: CircleX,
  },
  done: {
    strip: "border-[color:var(--status-completed-border)] bg-[color:var(--status-completed-bg)]",
    iconWrap: "bg-[color:var(--status-completed-dot)]/15",
    icon: "text-[color:var(--status-completed-fg)]",
    Icon: CheckCircle2,
  },
  rejected: {
    strip: "border-[color:var(--status-cancelled-border)] bg-[color:var(--status-cancelled-bg)]",
    iconWrap: "bg-[color:var(--status-cancelled-dot)]/15",
    icon: "text-[color:var(--status-cancelled-fg)]",
    Icon: CircleX,
  },
} as const;

/**
 * Sticky decision note. Actions sit beside the copy. No Update manually,
 * no source time. A waiting note that links to a pull request renders as
 * the pull-request review strip instead.
 */
function StoppedHeadline({ note }: { note: SplitRunFooterNote }) {
  if (!note.actor) {
    return note.headline;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1">
      <WorkOrderPersonMention person={note.actor} />
      <span>{note.headline}</span>
    </span>
  );
}

export function SplitRunAttentionNote({
  note,
  tone = "waiting",
  actions = [],
  runHref,
  actionBusy = false,
  startBusy = false,
  startDisabled = false,
  modelSelect,
  compact = false,
  actionsOnly = false,
  startEmphasis = "filled",
  organizationId,
  factoryId,
  orderId,
  pullRequests,
  canAct = true,
  onAction,
}: {
  note: SplitRunFooterNote;
  tone?: SplitRunDecisionTone;
  actions?: SplitRunFooterAction[];
  runHref?: string | null;
  actionBusy?: boolean;
  startBusy?: boolean;
  startDisabled?: boolean;
  modelSelect?: ReactNode;
  compact?: boolean;
  /** Refine strip: actions only, no note. The strip shows its own verdict. */
  actionsOnly?: boolean;
  /** Weight of Start on the refine strip. The verdict decides it. */
  startEmphasis?: StartEmphasis;
  organizationId?: string;
  factoryId?: string;
  orderId?: string;
  pullRequests?: FactoriesFactoryPullRequest[];
  canAct?: boolean;
  onAction?: (action: SplitRunFooterAction) => void;
}) {
  const pullRequestNote = WaitingPullRequestReview({
    note,
    tone,
    actions,
    actionBusy,
    compact,
    actionsOnly,
    organizationId,
    factoryId,
    orderId,
    pullRequests,
    canAct,
    onAction,
  });
  if (pullRequestNote) {
    return pullRequestNote;
  }

  return (
    <StandardAttentionNote
      note={note}
      tone={tone}
      actions={actions}
      runHref={runHref}
      actionBusy={actionBusy}
      startBusy={startBusy}
      startDisabled={startDisabled}
      modelSelect={modelSelect}
      compact={compact}
      actionsOnly={actionsOnly}
      startEmphasis={startEmphasis}
      onAction={onAction}
    />
  );
}

function StandardAttentionNote({
  note,
  tone = "waiting",
  actions = [],
  runHref,
  actionBusy = false,
  startBusy = false,
  startDisabled = false,
  modelSelect,
  compact = false,
  actionsOnly = false,
  startEmphasis = "filled",
  onAction,
}: {
  note: SplitRunFooterNote;
  tone?: SplitRunDecisionTone;
  actions?: SplitRunFooterAction[];
  runHref?: string | null;
  actionBusy?: boolean;
  startBusy?: boolean;
  startDisabled?: boolean;
  modelSelect?: ReactNode;
  compact?: boolean;
  actionsOnly?: boolean;
  startEmphasis?: StartEmphasis;
  onAction?: (action: SplitRunFooterAction) => void;
}) {
  if (actionsOnly) {
    return (
      <StripActions
        actions={actions}
        actionBusy={actionBusy}
        startBusy={startBusy}
        startDisabled={startDisabled}
        startEmphasis={startEmphasis}
        onAction={onAction}
      />
    );
  }

  if (compact) {
    return (
      <CompactAttentionNote
        note={note}
        actions={actions}
        runHref={runHref}
        actionBusy={actionBusy}
        startBusy={startBusy}
        startDisabled={startDisabled}
        modelSelect={modelSelect}
        onAction={onAction}
      />
    );
  }

  const visual = TONE[tone];
  const Icon = actions.some((action) => action.kind === "reopen") ? RotateCcw : visual.Icon;

  return (
    <div className={cn("border-t px-5 py-4", visual.strip)} data-testid="split-run-attention-note">
      <div className="flex items-center gap-3.5">
        <span
          className={cn("flex size-10 shrink-0 items-center justify-center rounded-full", visual.iconWrap)}
          aria-hidden
        >
          <Icon className={cn("size-5", visual.icon)} />
        </span>

        <div key={`${note.headline}-${note.text ?? ""}`} className="sp-text-reveal min-w-0 flex-1">
          <h3 className="workspace-section-title">
            <StoppedHeadline note={note} />
          </h3>
          {note.text ? (
            <div className="mt-1.5">
              <MarkdownContent
                content={note.text}
                variant="workspace"
                className="max-w-none text-[14px] leading-relaxed text-foreground/80"
              />
            </div>
          ) : null}
        </div>
        <NoteActionRow
          note={note}
          actions={actions}
          runHref={runHref}
          actionBusy={actionBusy}
          startBusy={startBusy}
          startDisabled={startDisabled}
          modelSelect={modelSelect}
          onAction={onAction}
        />
      </div>
    </div>
  );
}

function CompactAttentionNote({
  note,
  actions,
  runHref,
  actionBusy,
  startBusy,
  startDisabled,
  modelSelect,
  onAction,
}: {
  note: SplitRunFooterNote;
  actions: SplitRunFooterAction[];
  runHref?: string | null;
  actionBusy: boolean;
  startBusy: boolean;
  startDisabled: boolean;
  modelSelect?: ReactNode;
  onAction?: (action: SplitRunFooterAction) => void;
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3" data-testid="split-run-attention-note">
      <div
        key={`${note.headline}-${note.text ?? ""}`}
        className="sp-stream-text min-w-0 flex-1"
        data-testid="split-run-intent-decision-tip"
      >
        <h3 className="text-[13px] font-medium leading-5 text-foreground">
          <StoppedHeadline note={note} />
        </h3>
        {note.text ? <p className="mt-0.5 text-[12px] leading-4 text-muted-foreground">{note.text}</p> : null}
      </div>
      <NoteActionRow
        note={note}
        actions={actions}
        runHref={runHref}
        actionBusy={actionBusy}
        startBusy={startBusy}
        startDisabled={startDisabled}
        modelSelect={modelSelect}
        onAction={onAction}
      />
    </div>
  );
}

function NoteActionRow({
  note,
  actions,
  runHref,
  actionBusy,
  startBusy,
  startDisabled,
  modelSelect,
  onAction,
}: {
  note: SplitRunFooterNote;
  actions: SplitRunFooterAction[];
  runHref?: string | null;
  actionBusy: boolean;
  startBusy: boolean;
  startDisabled: boolean;
  modelSelect?: ReactNode;
  onAction?: (action: SplitRunFooterAction) => void;
}) {
  const href = note.cta?.href ?? runHref ?? undefined;
  const showCta = Boolean(note.cta && href);
  if (!showCta && actions.length === 0 && !modelSelect) {
    return null;
  }

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
      {showCta && href && note.cta ? <NoteCta label={note.cta.label} href={href} icon={note.cta.icon} /> : null}
      {actions.map((action) => {
        const groupedStart = action.kind === "start" && Boolean(modelSelect);
        const startLocked = startDisabled || startBusy || Boolean(action.disabled);
        const noteAction = (
          <NoteAction
            action={action}
            actionBusy={actionBusy}
            startBusy={startBusy}
            startDisabled={startDisabled}
            grouped={groupedStart}
            onClick={() => onAction?.(action)}
          />
        );
        if (!groupedStart) {
          return <Fragment key={action.id}>{noteAction}</Fragment>;
        }
        const select = isValidElement(modelSelect)
          ? cloneElement(modelSelect as ReactElement<{ disabled?: boolean }>, { disabled: startLocked })
          : modelSelect;
        return (
          <ButtonGroup key={action.id} aria-label="Start">
            {noteAction}
            <ButtonGroupSeparator className="bg-primary-foreground/25" />
            {select}
          </ButtonGroup>
        );
      })}
    </div>
  );
}

/**
 * Draft actions on the refine strip verdict row. No capsule, no border: the
 * verdict owns the row. Start is filled only when the verdict says go. The
 * model select lives on the strip settings row, not here.
 */
function StripActions({
  actions,
  actionBusy,
  startBusy,
  startDisabled,
  startEmphasis,
  onAction,
}: {
  actions: SplitRunFooterAction[];
  actionBusy: boolean;
  startBusy: boolean;
  startDisabled: boolean;
  startEmphasis: StartEmphasis;
  onAction?: (action: SplitRunFooterAction) => void;
}) {
  if (actions.length === 0) {
    return null;
  }
  const start = actions.find((action) => action.kind === "start");
  const rest = actions.filter((action) => action.kind !== "start");

  return (
    <div
      className="flex shrink-0 items-center gap-1.5"
      aria-label="Draft actions"
      data-testid="split-run-draft-action-group"
    >
      {rest.map((action) => (
        <NoteAction
          key={action.id}
          action={action}
          actionBusy={actionBusy}
          startBusy={startBusy}
          startDisabled={startDisabled}
          variant="ghost"
          onClick={() => onAction?.(action)}
        />
      ))}
      {start ? (
        <NoteAction
          action={start}
          actionBusy={actionBusy}
          startBusy={startBusy}
          startDisabled={startDisabled}
          variant={startEmphasis === "filled" ? "default" : "outline"}
          strip
          onClick={() => onAction?.(start)}
        />
      ) : null}
    </div>
  );
}

function NoteCta({ label, href, icon }: { label: string; href: string; icon?: "bug" }) {
  const external = href.startsWith("http");
  const mark = icon === "bug" ? <Bug className="size-3.5" aria-hidden /> : null;
  return (
    <Button asChild size="sm" variant="outline">
      {external ? (
        <a href={href} target="_blank" rel="noreferrer">
          {mark}
          {label}
          <ExternalLink className="size-3.5" aria-hidden />
        </a>
      ) : (
        <Link href={href}>
          {mark}
          {label}
        </Link>
      )}
    </Button>
  );
}

function ActionIcon({ kind, strip }: { kind?: SplitRunFooterAction["kind"]; strip?: boolean }) {
  if (strip && kind === "start") {
    return <Play className="size-3.5" aria-hidden />;
  }
  return null;
}

function NoteAction({
  action,
  actionBusy,
  startBusy,
  startDisabled,
  grouped = false,
  strip = false,
  variant,
  onClick,
}: {
  action: SplitRunFooterAction;
  actionBusy: boolean;
  startBusy: boolean;
  startDisabled: boolean;
  grouped?: boolean;
  /** On the refine strip Start carries a play icon. */
  strip?: boolean;
  /** Overrides the emphasis from the footer action. */
  variant?: "default" | "outline" | "ghost";
  onClick: () => void;
}) {
  const primary = action.emphasis === "primary";
  const busy = action.kind === "start" ? startBusy : actionBusy;
  const disabled = noteActionDisabled(action.kind, {
    actionBusy,
    startBusy,
    startDisabled,
    actionDisabled: action.disabled,
  });

  const button = (
    <Button
      type="button"
      size="sm"
      variant={variant ?? (primary ? "default" : "outline")}
      disabled={disabled}
      onClick={onClick}
      className={noteActionClassName({ grouped })}
      data-testid={primary ? "split-run-review-cta" : `split-run-footer-${action.id}`}
    >
      {busy ? (
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
      ) : (
        <ActionIcon kind={action.kind} strip={strip} />
      )}
      {action.label}
    </Button>
  );
  if (grouped || !action.tooltip) {
    return button;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>{disabled ? <span className="inline-flex">{button}</span> : button}</TooltipTrigger>
      <TooltipContent>{action.tooltip}</TooltipContent>
    </Tooltip>
  );
}
